import { captureException } from '@sentry/nextjs'

import localforage from 'localforage'
import { v4 as uuid } from 'uuid'

import { KEYS } from '~/behavior-tracking/constants/tracking-constants'
import { getStoreUser } from '~/redux/store'
import { getCookie, setCookie } from '~/utils/cookie.utils'

interface GeoLocationCookie {
  country: string | undefined
  city: string | undefined
}

interface TSession {
  id: number
  updatedAt: number
}

class TrackingEventStore {
  private store: LocalForage
  private isInitialized: Promise<boolean>
  private resolveInitStatus: (value: boolean) => void
  private readonly timeout = 1000 * 60 * 30 // 30 minutes in milliseconds
  private intervalID: number | NodeJS.Timeout | undefined

  public constructor() {
    try {
      this.store = localforage.createInstance({
        driver: localforage.LOCALSTORAGE,
        name: 'homTrackingEvents',
        storeName: 'metadata',
        description: 'tracking events metadata store',
      })
      this.isInitialized = new Promise((resolve) => {
        this.resolveInitStatus = resolve
      })
    } catch (e) {
      captureException(e, {
        tags: { source: 'TrackingBQ_TrackingEventStore' },
        level: 'error',
        extra: { message: 'failed to instantiate localforage store' },
      })
    }
  }

  public get ready(): Promise<boolean> {
    return this.isInitialized
  }

  public async getPseudoId(): Promise<string | null> {
    const isInitialized = await this.isInitialized
    if (isInitialized) {
      const pseudoId = await this.store?.getItem<string>(KEYS.PSEUDO_ID)
      const storeUser = getStoreUser()
      const userPseudoId = storeUser?.pseudoId
      if (userPseudoId && pseudoId !== userPseudoId) {
        await this.setPseudoId(userPseudoId)
        return userPseudoId
      }
      return pseudoId
    }
    return null
  }

  public async setPseudoId(userPseudoId?: string): Promise<void> {
    let pseudoId = userPseudoId ? userPseudoId : await this.store?.getItem<string>(KEYS.PSEUDO_ID)
    if (!pseudoId) {
      pseudoId = uuid()
    }
    await this.store?.setItem(KEYS.PSEUDO_ID, pseudoId)
    this.setPseudoIdCookie(pseudoId)
  }

  public async getSessionID(): Promise<number | void> {
    const isInitialized = await this.isInitialized
    if (!isInitialized) return

    const session = await this.handleSessionId()

    return session?.id as number
  }

  public async handleSessionId(): Promise<TSession> {
    const sessionData = await this.store?.getItem<TSession>(KEYS.SESSION_ID)

    if (typeof sessionData === 'number') {
      captureException(new Error('Session ID is not an object'), {
        tags: { source: 'TrackingBQ_TrackingEventStore' },
        extra: { message: 'Session ID is not an object' },
      })
      return await this.setSessionId()
    }

    const sessionId = sessionData?.id
    const missingCookie = !getCookie(KEYS.SESSION_ID)

    if (!sessionData || typeof sessionId !== 'number' || missingCookie) {
      // Set new session if missing from store or cookie
      return await this.setSessionId()
    }

    const updatedAt = sessionData.updatedAt || 0
    const now = Date.now()
    const diff = now - updatedAt

    if (diff > this.timeout || !sessionId) {
      // Set new session if it has expired
      return await this.setSessionId()
    }

    // Update session info
    const session = { id: sessionId, updatedAt: now }
    await this.store?.setItem(KEYS.SESSION_ID, session)
    return session
  }

  public resetSessionInterval(): void {
    if (typeof window === 'undefined') return
    this.handleStaleSession()
  }

  public async initState(): Promise<void> {
    try {
      // Await initialization of the store
      await this.isReady()
      // Initialize a new pseudoId if missing
      await this.setPseudoId()
      await this.handleSessionId()
      this.resetSessionInterval()
      this.resolveInitStatus(true)
    } catch (e) {
      this.resolveInitStatus(false)
      captureException(e, { tags: { source: 'TrackingBQ_TrackingEventStore' } })
    }
  }

  public getGeoLocation(): GeoLocationCookie | undefined {
    if (typeof window === 'undefined') return

    const country = getCookie('__geo_country')
    const city = getCookie('__geo_city')

    return { country: decodeURIComponent(country as string), city: decodeURIComponent(city as string) }
  }

  private async isReady(): Promise<void> {
    await this.store?.ready()
  }

  private handleStaleSession(): void {
    clearInterval(this.intervalID)

    this.intervalID = setInterval(() => {
      this.setSessionId()
    }, this.timeout)
  }

  private setSessionCookie(value: string): void {
    setCookie({ name: KEYS.SESSION_ID, value, expireInDays: 0 })
  }

  private setPseudoIdCookie(value: string): void {
    setCookie({ name: KEYS.PSEUDO_ID, value, expireInDays: 720 })
  }

  private async setSessionId(): Promise<TSession> {
    const id = Date.now()
    const session = await this.store?.setItem(KEYS.SESSION_ID, { id, updatedAt: id })
    this.setSessionCookie(id.toString())
    return session
  }
}

export const trackingEventStore = new TrackingEventStore()
